import {
  Organisation,
  OrgSiteListEntry,
} from "@inrange/building-manager-api-client/models-organisation";
import {
  getFinancialPerformance,
  getFinancialPerformanceProcurement,
} from "@inrange/building-manager-api-client/models-site";
import {
  calcBehindTheMeter,
  getScoreBySiteID,
  isSiteOwnerOccupied,
} from "@inrange/theme-components";
import calcSiteOwnershipTypeForOrg from "../utils/calcSiteOwnershipTypeForOrg";
import { getLatestActualsMonthIndex } from "./sitelist-utils";

// we use these weights to ensure these are sorted before inrange scores (5 is the max inrange score)
const siteStatusWeights: Record<string, number> = {
  // New statuses
  offerMade: 6,
  designsAndValidating: 7,
  pendingSignatures: 8,
  constructionPlanning: 9,
  installationInProgress: 10,
  // Legacy statuses
  contractSigned: 6,
  loaPending: 7,
  gridAppPendingSurveyInProgress: 8,
  gridAppPendingSurveyCompleted: 9,
  constructionPrelimsInProgress: 10,
  installationRoofWorks: 11,
  installationElectrical: 12,
  witnessTestingInProgress: 13,
  waitingOnDnoWitnessTest: 14,
};

export const sortByName = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  listedSites.sort((a, b) => {
    return sortAscending
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name);
  });
};

const sortByTenants = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): OrgSiteListEntry[] => {
  return listedSites.sort((a, b) => {
    const nameA =
      a.siteOwnerships
        .filter((so) => !so.isTest)
        .find((item) => item.ownership !== "landlord")?.name || "";
    const nameB =
      b.siteOwnerships
        .filter((so) => !so.isTest)
        .find((item) => item.ownership !== "landlord")?.name || "";
    const comparison = nameA.localeCompare(nameB);
    return sortAscending ? comparison : -comparison;
  });
};

export const sortByLandlords = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  listedSites.sort((a, b) => {
    return sortAscending
      ? a.landlordCount! - b.landlordCount!
      : b.landlordCount! - a.landlordCount!;
  });
};

export const sortByNetUsage = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) =>
    calcBehindTheMeter(site.energyFlowAnnual)
  );
};

export const sortByProperty = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean,
  propertyAccessor: (site: OrgSiteListEntry) => number
): void => {
  listedSites.sort((a, b) => {
    const propertyA = propertyAccessor(a);
    const propertyB = propertyAccessor(b);
    return sortAscending ? propertyA - propertyB : propertyB - propertyA;
  });
};

export const sortByTwoProperties = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean,
  propertyAccessor1: (site: OrgSiteListEntry) => number,
  propertyAccessor2: (site: OrgSiteListEntry) => number
): void => {
  listedSites.sort((a, b) => {
    const propertyA1 = propertyAccessor1(a);
    const propertyB1 = propertyAccessor1(b);
    if (propertyA1 !== propertyB1) {
      return sortAscending ? propertyA1 - propertyB1 : propertyB1 - propertyA1;
    }
    const propertyA2 = propertyAccessor2(a);
    const propertyB2 = propertyAccessor2(b);
    return sortAscending ? propertyA2 - propertyB2 : propertyB2 - propertyA2;
  });
};

export const sortByThreeProperties = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean,
  propertyAccessor1: (site: OrgSiteListEntry) => number,
  propertyAccessor2: (site: OrgSiteListEntry) => number,
  propertyAccessor3: (site: OrgSiteListEntry) => string
): void => {
  listedSites.sort((a, b) => {
    const propertyA1 = propertyAccessor1(a);
    const propertyB1 = propertyAccessor1(b);
    if (propertyA1 !== propertyB1) {
      return sortAscending ? propertyA1 - propertyB1 : propertyB1 - propertyA1;
    }
    const propertyA2 = propertyAccessor2(a);
    const propertyB2 = propertyAccessor2(b);
    if (propertyA2 !== propertyB2) {
      return sortAscending ? propertyA2 - propertyB2 : propertyB2 - propertyA2;
    }
    const propertyA3 = propertyAccessor3(a);
    const propertyB3 = propertyAccessor3(b);
    return sortAscending
      ? propertyB3.localeCompare(propertyA3)
      : propertyA3.localeCompare(propertyB3);
  });
};

export const sortByIRR = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
    return site.financialModels[isOwnerOccupier ? "ownerOccupier" : "landlord"]
      .license.irr!;
  });
};

const EXPORT_LIMIT_SORT_UNAVAILABLE = 1;
const EXPORT_LIMIT_SORT_COMING_SOON = 2;
const EXPORT_LIMIT_SORT_AVAILABLE = 3;

export const getExportLimitSortValue = (site: OrgSiteListEntry): number => {
  return site.totalExportLimit !== undefined && site.totalExportLimit !== null
    ? site.totalExportLimit > 0
      ? EXPORT_LIMIT_SORT_AVAILABLE
      : EXPORT_LIMIT_SORT_UNAVAILABLE
    : !site.substationData.success
      ? EXPORT_LIMIT_SORT_COMING_SOON
      : (site.substationData.headroom ?? 0 > 0)
        ? EXPORT_LIMIT_SORT_AVAILABLE
        : EXPORT_LIMIT_SORT_UNAVAILABLE;
};

export const sortByNetworkAvailabilityTenant = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByThreeProperties(
    listedSites,
    sortAscending,
    (site) => getExportLimitSortValue(site),
    (site) => {
      return site.financialModels.procurement.savings || 0;
    },
    (site) => site.name
  );
};

export const sortByNetworkAvailabilityLicense = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByThreeProperties(
    listedSites,
    sortAscending,
    (site) => getExportLimitSortValue(site),
    (site) => {
      const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
      return isOwnerOccupier
        ? site.financialModels.ownerOccupier.license.irr || 0
        : site.financialModels.landlord.license.irr || 0;
    },
    (site) => site.name
  );
};

export const sortByNetworkAvailabilityLease = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByThreeProperties(
    listedSites,
    sortAscending,
    (site) => getExportLimitSortValue(site),
    (site) => {
      const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
      return isOwnerOccupier
        ? site.financialModels.ownerOccupier.lease.revenue || 0
        : site.financialModels.landlord.lease.revenue || 0;
    },
    (site) => site.name
  );
};

export const sortBySiteStatusOrScoreTenant = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean,
  organisation: Organisation
): void => {
  sortByTwoProperties(
    listedSites,
    sortAscending,
    (site) => {
      const siteScore = getScoreBySiteID(organisation.id, site);
      const validStatus = site.operationalStatus !== "notStarted";
      return validStatus
        ? siteStatusWeights[site.operationalStatus]
        : siteScore || 0;
    },
    (site) => {
      return site.financialModels.procurement.savings;
    }
  );
};

export const sortBySiteStatusOrScoreLicense = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean,
  organisation: Organisation
): void => {
  sortByTwoProperties(
    listedSites,
    sortAscending,
    (site) => {
      const siteScore = getScoreBySiteID(organisation.id, site);
      const validStatus = site.operationalStatus !== "notStarted";
      return validStatus
        ? siteStatusWeights[site.operationalStatus]
        : siteScore || 0;
    },
    (site) => {
      const ownership = calcSiteOwnershipTypeForOrg(site, organisation.id);
      return site.financialModels[
        ownership === "ownerOccupier" ? "ownerOccupier" : "landlord"
      ].license.irr!;
    }
  );
};

export const sortBySiteStatusOrScoreLease = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean,
  organisation: Organisation
): void => {
  sortByTwoProperties(
    listedSites,
    sortAscending,
    (site) => {
      const siteScore = getScoreBySiteID(organisation.id, site);
      const validStatus = site.operationalStatus !== "notStarted";
      return validStatus
        ? siteStatusWeights[site.operationalStatus]
        : siteScore || 0;
    },
    (site) => {
      const ownership = calcSiteOwnershipTypeForOrg(site, organisation.id);
      return site.financialModels[
        ownership === "ownerOccupier" ? "ownerOccupier" : "landlord"
      ].lease.revenue;
    }
  );
};

export const sortByProjectCost = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => site.projectCosts.total);
};

export const sortByLastMonthCost = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships)!;
    const procurement = getFinancialPerformanceProcurement(
      site.energyFinancialsMonthly[lastMonthIndex].financial.actual,
      isOwnerOccupier,
      site.activeInvestmentModel
    )!;
    return procurement.costTotal;
  });
};

export const sortByLastMonthRevenueExport = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
    const revenue = getFinancialPerformance(
      site.energyFinancialsMonthly[lastMonthIndex].financial.actual,
      isOwnerOccupier,
      site.activeInvestmentModel
    )!;
    return revenue.revenueExport;
  });
};

export const sortByLastMonthProcurement = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const energy = site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
    return energy.procurement;
  });
};
export const sortByLastMonthRevenue = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
    const revenue = getFinancialPerformance(
      site.energyFinancialsMonthly[lastMonthIndex].financial.actual,
      isOwnerOccupier,
      site.activeInvestmentModel
    )!;
    return revenue.revenueTotal;
  });
};

export const sortByLastMonthSavings = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
    const savings = getFinancialPerformanceProcurement(
      site.energyFinancialsMonthly[lastMonthIndex].financial.actual,
      isOwnerOccupier,
      site.activeInvestmentModel
    )!;
    return savings.savings;
  });
};

export const sortByLastMonthDemandFulfilled = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const energy = site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
    return energy.procurement / energy.demand;
  });
};

export const sortByLastMonthGeneration = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const energy = site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
    return energy.generation;
  });
};

export const sortByLastMonthBehindMeter = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const energy = site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
    return energy.behindMeter;
  });
};

export const sortByLastMonthExport = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const lastMonthIndex = getLatestActualsMonthIndex(site);
    if (lastMonthIndex === undefined) {
      return 0;
    }
    const energy = site.energyFinancialsMonthly[lastMonthIndex].energy.actual!;
    return energy.exported + energy.networkExport;
  });
};

export const sortByOnsiteTariff = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(
    listedSites,
    sortAscending,
    (site) => site.tariffConfig.onsitePPA.tariff
  );
};

export const sortByLicenseRevenue = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
    return isOwnerOccupier
      ? site.financialModels.ownerOccupier.license.revenue
      : site.financialModels.landlord.license.revenue;
  });
};

export const sortByLeaseRevenue = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    const isOwnerOccupier = isSiteOwnerOccupied(site.siteOwnerships);
    return isOwnerOccupier
      ? site.financialModels.ownerOccupier.lease.revenue
      : site.financialModels.landlord.lease.revenue;
  });
};

export const sortByCarbonSavings = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(
    listedSites,
    sortAscending,
    (site) => site.calculations.emissionsAvoided.totalAvoidance
  );
};

export const sortByRoofArea = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => site.totalBuildingArea);
};

export const sortByleaseRevenuePerArea = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  listedSites.sort((a, b) => {
    const isOwnerOccupierA = isSiteOwnerOccupied(a.siteOwnerships);
    const leaseRevenuePerAreaA = isOwnerOccupierA
      ? a.financialModels.ownerOccupier.lease.revenue / a.totalBuildingArea
      : a.financialModels.landlord.lease.revenue / a.totalBuildingArea;

    const isOwnerOccupierB = isSiteOwnerOccupied(b.siteOwnerships);
    const leaseRevenuePerAreaB = isOwnerOccupierB
      ? b.financialModels.ownerOccupier.lease.revenue / b.totalBuildingArea
      : b.financialModels.landlord.lease.revenue / b.totalBuildingArea;

    return sortAscending
      ? leaseRevenuePerAreaA - leaseRevenuePerAreaB
      : leaseRevenuePerAreaB - leaseRevenuePerAreaA;
  });
};

export const sortByPpaSavings = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  sortByProperty(listedSites, sortAscending, (site) => {
    if (
      isSiteOwnerOccupied(site.siteOwnerships) &&
      site.activeInvestmentModel === "license"
    ) {
      return site.financialModels.ooLicenseProcurement.savingsPerPpa.reduce(
        (acc, ppa) => acc + ppa,
        0
      );
    } else {
      return site.financialModels.procurement.savingsPerPpa.reduce(
        (acc, ppa) => acc + ppa,
        0
      );
    }
  });
};

export const sortByEnergyProcured = (
  listedSites: OrgSiteListEntry[],
  sortAscending: boolean
): void => {
  listedSites.sort((a, b) => {
    return sortAscending
      ? a.energyFlowAnnual.procurement - b.energyFlowAnnual.procurement
      : b.energyFlowAnnual.procurement - a.energyFlowAnnual.procurement;
  });
};

export function sortByColumn(
  listedSites: OrgSiteListEntry[],
  sortColumn: string,
  sortAscending: boolean,
  organisation: Organisation
) {
  const sortFunctions = {
    name: sortByName,
    irr: sortByIRR,
    networkAvailabilityTenant: sortByNetworkAvailabilityTenant,
    networkAvailabilityLicense: sortByNetworkAvailabilityLicense,
    networkAvailabilityLease: sortByNetworkAvailabilityLease,
    projectCost: sortByProjectCost,
    onsiteTariff: sortByOnsiteTariff,
    licenseRevenue: sortByLicenseRevenue,
    tenants: sortByTenants,
    carbonSavings: sortByCarbonSavings,
    roofArea: sortByRoofArea,
    leaseRevenue: sortByLeaseRevenue,
    leaseRevenuePerArea: sortByleaseRevenuePerArea,
    ppaSavings: sortByPpaSavings,
    landlords: sortByLandlords,
    netUsage: sortByNetUsage,
    energyProcured: sortByEnergyProcured,
    siteStatusTenant: sortBySiteStatusOrScoreTenant,
    siteStatusLicense: sortBySiteStatusOrScoreLicense,
    siteStatusLease: sortBySiteStatusOrScoreLease,
    lastMonthCost: sortByLastMonthCost,
    lastMonthRevenueExport: sortByLastMonthRevenueExport,
    lastMonthProcurement: sortByLastMonthProcurement,
    lastMonthRevenue: sortByLastMonthRevenue,
    lastMonthSavings: sortByLastMonthSavings,
    lastMonthDemandFulfilled: sortByLastMonthDemandFulfilled,
    lastMonthGeneration: sortByLastMonthGeneration,
    lastMonthSoldOnsite: sortByLastMonthBehindMeter,
    lastMonthSoldExport: sortByLastMonthExport,
  };

  const sortFunction = sortFunctions[sortColumn];

  if (sortFunction) {
    sortFunction(listedSites, sortAscending, organisation);
  }
}

export const filterSitesByText = (
  sites: OrgSiteListEntry[],
  textFilter: string
) => {
  return sites.filter(
    (site) =>
      site.name.toLowerCase().includes(textFilter.toLowerCase()) ||
      site.address.toLowerCase().includes(textFilter.toLowerCase()) ||
      site.siteOwnerships
        .filter((so) => !so.isTest)
        .map((ownership) => ownership.name!.toLowerCase())
        .join(" ")
        .includes(textFilter.toLowerCase())
  );
};

export const togglesortAscending = (setSortAscending) => {
  setSortAscending((prevsortAscending) => !prevsortAscending);
};

// if true default sort will be in ascending order.
const defaultSortAscendingValues = {
  name: true,
  projectCost: true,
  onsiteTariff: true,
  tenants: true,
  landlords: true,
  netUsage: true,
  // File fields
  description: true,
  number: true,
};

export const sortAscendingForColumn = (column: string) => {
  return !!defaultSortAscendingValues[column];
};
